import { format } from 'date-fns'
import { formatToTime } from './formatToTime'
import { getMinutesFromString } from './getMinutesFromString'

const commonSelectNormalizeItem = (item) => {
  item.id = item.id ?? item.slug
  item.value = item.value ?? item.label ?? item.id
  item.label = item.label ?? item.value ?? item.id
  return item
}

export const commonSelectsDefaultMapper = (
  { id, slug, name },
  withId = false
) => {
  const optionLabel = withId ? `#${id} ${name}` : name

  return {
    id: id || slug,
    value: optionLabel,
    label: optionLabel,
  }
}

export const prepareCommonSelects = (data, withIdOrMapper = false) => {
  if (!data) {
    return data
  }
  if (Array.isArray(data)) {
    return data.map((item) => prepareCommonSelects(item, withIdOrMapper))
  } else {
    return typeof withIdOrMapper == 'boolean'
      ? commonSelectsDefaultMapper(data, withIdOrMapper)
      : commonSelectNormalizeItem(withIdOrMapper(data))
  }
}

export const prepareRoleList = (role) => {
  return role?.length > 0
    ? role.map(({ id, name }) => {
        return {
          id: id,
          value: name,
          label: name,
        }
      })
    : []
}

export const prepareStuff = (users) => {
  const preparedStuff = []

  users.forEach((item) => {
    const exsistingIndex = preparedStuff.findIndex(
      (user) => user.id === item.id
    )

    if (exsistingIndex >= 0) {
      preparedStuff[exsistingIndex].role.push(item.role)
    } else {
      preparedStuff.push({
        ...item,
        role: [item.role],
      })
    }
  })

  return preparedStuff
}

const prepareTaskEstimate = (estimate) => ({
  ...estimate,
  estimate: getMinutesFromString(estimate.estimate),
})

export function prepareTaskEstimates(estimates) {
  if (!Array.isArray(estimates)) {
    return prepareTaskEstimate(estimates)
  }
  return estimates.map(prepareTaskEstimate)
}

const TASKS_WITHOUT_ESTIMATES = ['Эпик']
export const prepareTaskValues = (values, type, stage, isAdminOrManager) => {
  const prepared = Object.fromEntries(
    Object.entries(values).map((entity) => {
      return entity.map((value) => {
        if (
          typeof value === 'object' &&
          value !== null &&
          !Array.isArray(value)
        ) {
          return value.id
        } else {
          return value
        }
      })
    })
  )
  //установка статуса задачи в бэклог если поля stage есть в сторе
  if (stage) {
    prepared.stage_id = 101
  }

  prepared.estimates = prepareTaskEstimates(prepared.estimates ?? [])

  if (!prepared.estimate_cost) {
    prepared.estimate_cost = 0
  } else {
    prepared.estimate_cost = getMinutesFromString(prepared.estimate_cost)
  }

  prepared.estimate_cost_management = prepared.estimate_cost_management
    ? getMinutesFromString(prepared.estimate_cost_management)
    : undefined

  prepared.estimate_cost_test = prepared.estimate_cost_test
    ? getMinutesFromString(prepared.estimate_cost_test)
    : undefined

  if ((type === 'create' && !prepared.estimate_cost) || !isAdminOrManager) {
    delete prepared.estimate_cost
  }
  const skipEstimates =
    !isAdminOrManager ||
    TASKS_WITHOUT_ESTIMATES.includes(values.task_type_id?.value)
  if (skipEstimates) {
    delete prepared.is_overtime
    delete prepared.estimates
  }
  if (type == 'edit') {
    // оценки редактируются и сохраняются самостоятельно не в составе всей формы
    delete prepared.estimates
  }
  return prepared
}

export const makeFullName = ({ name, surname, patronymic }) =>
  [surname, name, patronymic].filter(Boolean).join(' ').trim() || ''

export const prepareUserIdList = (users) => {
  return users?.length > 0 ? users.map(({ id }) => id) : []
}

export const getNonServiceUsers = (users = []) => {
  return users.filter(({ is_service }) => !is_service)
}

export const prepareUserOption = ({ id, name, surname }) => {
  const fullName = makeFullName({ name, surname })

  return {
    id,
    value: fullName,
    label: fullName,
  }
}

export const prepareUserList = (users) => {
  return getNonServiceUsers(users).map(prepareUserOption)
}

export const prepareList = (items, visibleId = false) => {
  return items?.length > 0
    ? items.map(({ id, name }) => {
        return {
          id: id,
          value: visibleId ? `#${id} ${name}` : name,
          label: visibleId ? `#${id} ${name}` : name,
        }
      })
    : []
}

export const prepareCurrentUserRoles = (staff, id, roleList) => {
  return staff
    .find((employee) => employee.id === id)
    .role.map((item) => {
      return roleList.find(({ id }) => id === item)
    })
    .reduce((previousValue, currentValue) => {
      previousValue.push(currentValue?.name)
      return previousValue
    }, [])
    .join(' ')
}

export function prepareDate(date) {
  if (typeof date == 'string' && !date.match(/^\d\d\.\d\d\.\d\d\d\d$/))
    return format(new Date(date), 'dd.MM.yyyy')
  else return date
}

export const adaptTaskEstimates = (estimates) => {
  return estimates.map((estimate) => ({
    ...estimate,
    date_start: prepareDate(estimate.date_start),
    date_end: prepareDate(estimate.date_end),
    estimate:
      estimate.estimate == 0
        ? undefined
        : formatToTime(estimate.estimate).replace(/\s/g, ''),
  }))
}

const getCollator = (locales) => {
  return new Intl.Collator(locales, {
    numeric: true,
    sensitivity: 'base',
    ignorePunctuation: true,
  })
}

export const sortOptionsAsc = (arr, locales = ['ru', 'en']) => {
  const collator = getCollator(locales)
  return arr.sort((a, b) => {
    const av = a.value.trim()
    const bv = b.value.trim()
    return collator.compare(av, bv)
  })
}

export const normalizeForFilter = (option, inputValue) =>
  option
    .toString()
    .toLowerCase()
    .replace(/ё/g, 'е')
    .includes(inputValue.toLowerCase().replace(/ё/g, 'е'))

export const pluralizeHours = (hours) => {
  const mod100 = hours % 100
  if (mod100 >= 11 && mod100 <= 14) return 'часов'

  const mod10 = hours % 10
  if (mod10 === 1) return 'час'
  if (mod10 >= 2 && mod10 <= 4) return 'часа'
  return 'часов'
}

export const capitalizeFirstLetterSafe = (value) => {
  const str = value ?? ''
  if (!str) return ''

  const firstChar = str[0]
  const isLetter = firstChar.toLowerCase() !== firstChar.toUpperCase()

  if (!isLetter) return str

  return firstChar.toLocaleUpperCase() + str.slice(1)
}
